import { useAuth } from 'src/util/authenticationProvider';
import { useCallback, useEffect, useMemo, useState, FormEvent, ChangeEvent } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { AccountData } from 'src/util/types';
import config from '../../../../config';
import { useTranslation } from 'react-i18next';

type AccountFormState = {
  username: string;
  password: string;
  firstname: string;
  lastname: string;
  email: string;
  userType: string;
};

const defaultFormState: AccountFormState = {
  username: '',
  password: '',
  firstname: '',
  lastname: '',
  email: '',
  userType: 'EMPLOYEE'
};

const resolveUserType = (account: AccountData) => {
  if (account.userType) {
    return account.userType;
  }
  if (account.roles?.includes('tenant-owner')) return 'OWNER';
  if (account.roles?.includes('tenant-admin')) return 'ADMIN';
  if (account.roles?.includes('tenant-employee')) return 'EMPLOYEE';
  return 'EMPLOYEE';
};

export default function UserManagement() {
  const authContext = useAuth();
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<AccountData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountData | null>(null);
  const [formState, setFormState] = useState<AccountFormState>(defaultFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [listSuccess, setListSuccess] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const accessToken = authContext.tokens?.accessToken;
  const headers = useMemo(() => (accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined), [accessToken]);

  const fetchAccounts = useCallback(async () => {
    if (!headers) {
      setAccounts([]);
      return;
    }
    setLoading(true);
    setListError(null);
    try {
      const response = await axios.get(config.apiUrl + '/api/accounts/all', { headers });
      setAccounts(response.data.content ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const openCreateDialog = () => {
    setEditingAccount(null);
    setFormState(defaultFormState);
    setFormError(null);
    setFormSuccess(null);
    setDialogOpen(true);
  };

  const openEditDialog = (account: AccountData) => {
    const userType = resolveUserType(account);
    setEditingAccount(account);
    setFormState({
      username: account.username ?? '',
      password: '',
      firstname: account.profile?.firstname ?? '',
      lastname: account.profile?.lastname ?? '',
      email: account.email ?? '',
      userType
    });
    setFormError(null);
    setFormSuccess(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingAccount(null);
    setFormState(defaultFormState);
    setFormError(null);
    setFormSuccess(null);
    setListSuccess(null);
    setListError(null);
  };

  const handleInputChange = (field: keyof AccountFormState) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.value }));
  };


  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!headers) return;
    setFormError(null);
    setFormSuccess(null);

    if (!formState.username.trim() || !formState.email.trim()) {
      setFormError(t('users.errors.usernameEmailRequired'));
      return;
    }
    if (!editingAccount && !formState.password.trim()) {
      setFormError(t('users.errors.passwordRequired'));
      return;
    }

    const payload: any = {
      username: formState.username.trim(),
      profile: {
        firstname: formState.firstname.trim(),
        lastname: formState.lastname.trim()
      },
      email: formState.email.trim(),
      userType: formState.userType
    };

    if (formState.password.trim()) {
      payload.password = formState.password.trim();
    }

    try {
      if (editingAccount?.id) {
        await axios.put(`${config.apiUrl}/api/accounts/update/${encodeURIComponent(editingAccount.id)}`, payload, {
          headers
        });
        setFormSuccess(t('users.success.updated'));
      } else {
        await axios.post(`${config.apiUrl}/api/accounts/create`, payload, { headers });
        setFormSuccess(t('users.success.created'));
      }
      await fetchAccounts();
      setTimeout(() => {
        closeDialog();
      }, 800);
    } catch (err: any) {
      setFormError(err?.response?.data?.message || t('users.errors.saveFailed'));
    }
  };

  const handleDelete = async (account: AccountData) => {
    if (!headers) return;
    const confirmed = window.confirm(t('users.deleteConfirm', { username: account.username }));
    if (!confirmed) return;
    setListError(null);
    setListSuccess(null);
    setLoading(true);
    try {
      await axios.delete(`${config.apiUrl}/api/accounts/delete/${encodeURIComponent(account.id)}`, { headers });
      setListSuccess(t('users.success.deleted'));
      await fetchAccounts();
    } catch (err: any) {
      setListError(err?.response?.data?.message || t('users.errors.deleteFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t('title.Accounts')}
        action={
          <Button variant="contained" onClick={openCreateDialog}>
            {t('general.Create')}
          </Button>
        }
      />
      <CardContent>
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}
        {listSuccess && <Alert severity="success" sx={{ mb: 2 }}>{listSuccess}</Alert>}
        {loading ? (
          <Typography>{t('general.Loading')}</Typography>
        ) : accounts.length === 0 ? (
          <Typography>{t('users.empty')}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('general.Username')}</TableCell>
                  <TableCell>{t('users.fields.email')}</TableCell>
                  <TableCell>{t('users.fields.firstName')}</TableCell>
                  <TableCell>{t('users.fields.lastName')}</TableCell>
                  <TableCell>{t('users.fields.userType')}</TableCell>
                  <TableCell align="right">{t('general.Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.username}</TableCell>
                    <TableCell>{account.email}</TableCell>
                    <TableCell>{account.profile?.firstname}</TableCell>
                    <TableCell>{account.profile?.lastname}</TableCell>
                    <TableCell>{resolveUserType(account)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => openEditDialog(account)}>
                          {t('general.Edit')}
                        </Button>
                        <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(account)}>
                          {t('general.Delete')}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAccount ? t('users.dialog.updateTitle') : t('users.dialog.createTitle')}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label={t('general.Username')}
                value={formState.username}
                onChange={handleInputChange('username')}
                required
                fullWidth
              />
              <TextField
                label={t('general.Password')}
                type="password"
                value={formState.password}
                onChange={handleInputChange('password')}
                required={!editingAccount}
                fullWidth
                helperText={editingAccount ? t('users.passwordHint') : undefined}
              />
              <TextField
                label={t('users.fields.firstName')}
                value={formState.firstname}
                onChange={handleInputChange('firstname')}
                fullWidth
              />
              <TextField
                label={t('users.fields.lastName')}
                value={formState.lastname}
                onChange={handleInputChange('lastname')}
                fullWidth
              />
              <TextField
                label={t('users.fields.email')}
                type="email"
                value={formState.email}
                onChange={handleInputChange('email')}
                required
                fullWidth
              />
              <TextField
                select
                label={t('users.fields.userType')}
                value={formState.userType}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setFormState((prev) => ({ ...prev, userType: event.target.value }))
                }
                fullWidth
              >
                <MenuItem value="ADMIN">{t('users.userType.admin')}</MenuItem>
                <MenuItem value="EMPLOYEE">{t('users.userType.employee')}</MenuItem>
              </TextField>
              {formError && <Alert severity="error">{formError}</Alert>}
              {formSuccess && <Alert severity="success">{formSuccess}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>{t('general.Cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingAccount ? t('general.Save') : t('general.Create')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Card>
  );
}
